import React, { useMemo, useState } from 'react';
import { useLms } from '../../context/LmsContext';
import { Card, DataTable, FormRow, PageHeader, RoleBadge, StatusBadge } from '../../components/ui';
import { fullName } from '../../utils/lmsSelectors';
import { normalizeUserRoles, roleOptions } from '../../utils/navigation';

const csvTemplate = `email,full_name,roles,password,team,title\njhunel@creng.solar,Jhunel Bernales,"learner,trainer,admin",Solar123,Leadership,LMS Administrator\nlearner1@creng.solar,Learner One,learner,ChangeMe123!,Design Team,Learner\ntrainer1@creng.solar,Trainer One,"trainer,learner",ChangeMe123!,Training,Trainer\n`;

function roleLabel(role) {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function cleanRoles(roles) {
  const selected = roleOptions.filter((role) => roles.includes(role));
  return selected.length ? selected : ['learner'];
}

function parseRoleText(value, fallback = 'learner') {
  const rawRoles = String(value || fallback).split(/[|,;]/).map((role) => role.trim().toLowerCase()).filter(Boolean);
  return cleanRoles(rawRoles);
}

function parseCsvLine(line) {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];
    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  values.push(current.trim());
  return values;
}

function normalizeHeader(header) {
  return String(header || '').trim().toLowerCase().replace(/\s+/g, '_');
}

function nameParts(row) {
  const firstName = row.first_name || row.firstname || '';
  const lastName = row.last_name || row.lastname || '';
  if (firstName || lastName) return { firstName, lastName };

  const fullName = row.full_name || row.fullname || row.name || '';
  const parts = fullName.split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ')
  };
}

function parseUsersCsv(text) {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];

  const headers = parseCsvLine(lines[0]).map(normalizeHeader);
  return lines.slice(1).map((line, index) => {
    const values = parseCsvLine(line);
    const raw = Object.fromEntries(headers.map((header, headerIndex) => [header, values[headerIndex] || '']));
    const { firstName, lastName } = nameParts(raw);
    const roles = parseRoleText(raw.roles || raw.role || 'learner');
    const requestedRole = String(raw.role || '').trim().toLowerCase();
    const role = roles.includes(requestedRole) ? requestedRole : roles[0];
    const password = raw.password || raw.temporary_password || raw.temporarypassword || '';
    const email = String(raw.email || '').trim().toLowerCase();
    const errors = [];

    if (!email || !email.includes('@')) errors.push('Missing valid email');
    if (!password || password.length < 6) errors.push('Password must be at least 6 characters');

    return {
      rowNumber: index + 2,
      email,
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`.trim(),
      role,
      roles,
      password,
      team: raw.team || 'Unassigned',
      title: raw.title || role,
      status: raw.status || 'active',
      timezone: raw.timezone || 'Asia/Manila',
      errors
    };
  });
}

function RoleAccessEditor({ user, onUpdate }) {
  const userRoles = normalizeUserRoles(user);

  const toggleRole = (role, checked) => {
    const nextRoles = cleanRoles(checked ? [...userRoles, role] : userRoles.filter((item) => item !== role));
    const nextActiveRole = nextRoles.includes(user.role) ? user.role : nextRoles[0];
    onUpdate(user.id, { roles: nextRoles, role: nextActiveRole });
  };

  return (
    <div className="role-checkboxes compact-role-checkboxes">
      {roleOptions.map((role) => (
        <label key={role} className="check-row compact-check-row">
          <input type="checkbox" checked={userRoles.includes(role)} onChange={(event) => toggleRole(role, event.target.checked)} />
          {roleLabel(role)}
        </label>
      ))}
    </div>
  );
}

export default function AdminUsers() {
  const { data, currentUser, createUser, bulkImportUsers, updateUser } = useLms();
  const [query, setQuery] = useState('');
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: 'ChangeMe123!',
    role: 'learner',
    roles: ['learner'],
    team: 'Design Team',
    title: ''
  });
  const [message, setMessage] = useState('');
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkImporting, setBulkImporting] = useState(false);

  const rows = useMemo(() => data.users.filter((user) => `${user.email} ${fullName(user)} ${user.team}`.toLowerCase().includes(query.toLowerCase())), [data.users, query]);
  const selectedRoles = cleanRoles(form.roles);
  const validBulkRows = useMemo(() => bulkRows.filter((row) => !row.errors.length), [bulkRows]);
  const invalidBulkRows = bulkRows.length - validBulkRows.length;

  const setCreateRole = (role, checked) => {
    const nextRoles = cleanRoles(checked ? [...selectedRoles, role] : selectedRoles.filter((item) => item !== role));
    setForm({ ...form, roles: nextRoles, role: nextRoles.includes(form.role) ? form.role : nextRoles[0] });
  };

  const updateUserAccess = (userId, patch) => {
    updateUser(userId, patch, currentUser.id);
  };

  const submit = async (event) => {
    event.preventDefault();
    const payload = { ...form, roles: selectedRoles, role: selectedRoles.includes(form.role) ? form.role : selectedRoles[0] };
    const result = await createUser(payload, currentUser.id);
    setMessage(result.ok ? `Created ${form.email}` : result.message);
    if (result.ok) setForm({ firstName: '', lastName: '', email: '', password: 'ChangeMe123!', role: 'learner', roles: ['learner'], team: 'Design Team', title: '' });
  };

  const handleCsvFile = async (event) => {
    const file = event.target.files?.[0];
    setBulkResults([]);
    if (!file) {
      setBulkRows([]);
      setBulkMessage('');
      return;
    }

    const text = await file.text();
    const parsedRows = parseUsersCsv(text);
    setBulkRows(parsedRows);
    setBulkMessage(parsedRows.length ? `Loaded ${parsedRows.length} CSV rows. Review the preview, then click Import users.` : 'No user rows were found in the CSV.');
  };

  const importCsvUsers = async () => {
    if (!validBulkRows.length) {
      setBulkMessage('No valid users to import. Fix the CSV and upload it again.');
      return;
    }

    setBulkImporting(true);
    setBulkMessage('Importing users...');
    const result = await bulkImportUsers(validBulkRows, currentUser.id);
    setBulkImporting(false);
    setBulkResults(result.results || []);
    const summary = result.summary;
    if (summary) {
      setBulkMessage(`${result.message} Created: ${summary.created}. Updated: ${summary.updated}. Failed: ${summary.failed}.`);
    } else {
      setBulkMessage(result.message);
    }
  };

  return (
    <>
      <PageHeader title="User management" description="Create, edit, deactivate, and organize internal LMS users. Assign one or more role abilities to each user." />
      <div className="grid two uneven">
        <Card>
          <h2>Create user</h2>
          <form className="form-stack" onSubmit={submit}>
            <div className="form-grid">
              <FormRow label="First name"><input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></FormRow>
              <FormRow label="Last name"><input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></FormRow>
            </div>
            <FormRow label="Email"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></FormRow>
            <FormRow label="Temporary password"><input required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></FormRow>
            <div className="form-grid">
              <FormRow label="Current role">
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  {selectedRoles.map((role) => <option key={role} value={role}>{roleLabel(role)}</option>)}
                </select>
              </FormRow>
              <FormRow label="Team"><input value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} /></FormRow>
            </div>
            <div>
              <span className="form-row-title">Allowed roles</span>
              <div className="role-checkboxes">
                {roleOptions.map((role) => (
                  <label key={role} className="check-row">
                    <input type="checkbox" checked={selectedRoles.includes(role)} onChange={(event) => setCreateRole(role, event.target.checked)} />
                    {roleLabel(role)}
                  </label>
                ))}
              </div>
            </div>
            <FormRow label="Title"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormRow>
            <button className="button button-primary" type="submit">Create user</button>
            {message && <p className="muted small">{message}</p>}
          </form>
        </Card>

        <Card>
          <h2>Bulk upload CSV</h2>
          <div className="form-stack">
            <p className="muted">Upload one CSV file to create or update many Supabase Auth users and LMS profiles at once.</p>
            <a className="button button-secondary" href={`data:text/csv;charset=utf-8,${encodeURIComponent(csvTemplate)}`} download="lms-user-import-template.csv">Download CSV template</a>
            <FormRow label="CSV file">
              <input type="file" accept=".csv,text/csv" onChange={handleCsvFile} />
            </FormRow>
            <div className="import-summary">
              <strong>{bulkRows.length} rows loaded</strong>
              <span>{validBulkRows.length} valid</span>
              <span>{invalidBulkRows} need attention</span>
            </div>
            <button className="button button-primary" type="button" disabled={!validBulkRows.length || bulkImporting} onClick={importCsvUsers}>
              {bulkImporting ? 'Importing...' : 'Import users'}
            </button>
            {bulkMessage && <p className="muted small">{bulkMessage}</p>}
          </div>
        </Card>
      </div>

      <Card>
        <h2>Access policy</h2>
        <p>A user can have learner, trainer, and admin abilities at the same time. User creation now creates a Supabase Auth account through an Edge Function, then saves the LMS role profile.</p>
        <ul className="check-list">
          <li>Admins see all users, reports, courses, assignments, and certificates.</li>
          <li>Trainers see course builder, owned courses, and review queue items.</li>
          <li>Learners see assigned courses, grades, and certificates.</li>
        </ul>
      </Card>

      {bulkRows.length > 0 && (
        <Card>
          <h2>CSV preview</h2>
          <DataTable
            rows={bulkRows.slice(0, 25)}
            columns={[
              { key: 'rowNumber', label: 'Row' },
              { key: 'email', label: 'Email' },
              { key: 'fullName', label: 'Name' },
              { key: 'roles', label: 'Allowed roles', render: (row) => row.roles.map(roleLabel).join(', ') },
              { key: 'status', label: 'Status', render: (row) => row.errors.length ? <StatusBadge status="failed" /> : <StatusBadge status="ready" /> },
              { key: 'errors', label: 'Notes', render: (row) => row.errors.length ? row.errors.join('; ') : 'Ready to import' }
            ]}
          />
          {bulkRows.length > 25 && <p className="muted small">Showing first 25 rows only.</p>}
        </Card>
      )}

      {bulkResults.length > 0 && (
        <Card>
          <h2>Import results</h2>
          <DataTable
            rows={bulkResults}
            columns={[
              { key: 'rowNumber', label: 'Row' },
              { key: 'email', label: 'Email' },
              { key: 'status', label: 'Result', render: (row) => <StatusBadge status={row.status} /> },
              { key: 'message', label: 'Message' }
            ]}
          />
        </Card>
      )}

      <Card>
        <div className="toolbar"><input placeholder="Search users..." value={query} onChange={(e) => setQuery(e.target.value)} /></div>
        <DataTable
          rows={rows}
          columns={[
            { key: 'name', label: 'Name', render: (row) => fullName(row) },
            { key: 'email', label: 'Email' },
            { key: 'role', label: 'Current role', render: (row) => <RoleBadge role={row.role} /> },
            { key: 'roles', label: 'Allowed roles', render: (row) => <RoleAccessEditor user={row} onUpdate={updateUserAccess} /> },
            { key: 'team', label: 'Team' },
            { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
            { key: 'action', label: 'Action', render: (row) => <button className="button button-ghost" onClick={() => updateUser(row.id, { status: row.status === 'active' ? 'inactive' : 'active' }, currentUser.id)}>{row.status === 'active' ? 'Deactivate' : 'Activate'}</button> }
          ]}
        />
      </Card>
    </>
  );
}
